<template>
    <div :class="['shield-button',`shield-button--${props.type}`]" role="button" tabindex="0" aria-label="Scroll down to additional product content">
        <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" id="dynamicSvg">
            <polygon
                points="100,0 100,75 50,100 0,75 0,0"
                id="outerPolygon"
            />
            <polygon
                points="100,0 100,75 50,100 0,75 0,0"
                id="innerPolygon"
            />
            <foreignObject x="0" y="20" width="100" height="100" id="foreign">
                <div xmlns="http://www.w3.org/1999/xhtml" id="content">
                    <slot></slot>
                </div>
            </foreignObject>
        </svg>
    </div>
</template>
<script setup lang="ts">
    import { watch, onMounted, onBeforeUnmount } from "vue";
    import { useRothcoBreakpoints } from "@/helpers/composables/useBreakpoints";

    let props = defineProps({
        type: {
            type: String,
            default: 'primary'
        },
    })

    let observer: MutationObserver;

    onMounted(() => {
        if(breakpoints.isGreaterOrEqual('laptop')){
            resizeSVGToFitContent();
        }
        const content = document.getElementById("content");
        if (content) {
            observer = new MutationObserver(() => {
                resizeSVGToFitContent();
            });
            observer.observe(content, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }

        window.addEventListener("load", resizeSVGToFitContent);
    });

    onBeforeUnmount(() => {
        if (observer) observer.disconnect();
        window.removeEventListener("load", resizeSVGToFitContent);
    });

    const breakpoints = useRothcoBreakpoints();
    watch(()=>(breakpoints), (newVal)=>{
        if(newVal.isGreaterOrEqual('laptop')){
            resizeSVGToFitContent();
        }
    },{deep: true})


    const resizeSVGToFitContent = ()=>{
        const svg = document.getElementById("dynamicSvg");
        const content = document.getElementById("content");
        const foreign = document.getElementById("foreign");
        const outerPolygon = document.getElementById("outerPolygon");
        const innerPolygon = document.getElementById("innerPolygon");

        // Wait for rendering
        requestAnimationFrame(() => {
            const heightPadding = 40;

            // Measure content size
            const contentBox = content.getBoundingClientRect();
            const newWidth = content.scrollWidth;
            const newHeight = content.scrollHeight + heightPadding;

            // Update actual display size
            svg.style.width = `${newWidth}px`;
            svg.style.height = `${newHeight}px`;

            // Resize foreignObject to fit new area
            foreign.setAttribute("width", newWidth);
            foreign.setAttribute("height", newHeight);

            // Set viewBox to match size in virtual SVG units
            svg.setAttribute("viewBox", `0 0 ${newWidth} ${newHeight}`);

            // Polygon based on new width/height
            const w = newWidth;
            const h = newHeight;
            
            const offset = -5;
            outerPolygon.setAttribute("points", getPolygonCoords(w,h,0));
            innerPolygon.setAttribute("points", getPolygonCoords(w,h,offset));
        });
    }

    const getPolygonCoords = (w, h, offset) => {
        let points = [
            [w, 0],          // Top-right corner
            [w, h * 0.75],   // Bottom-right edge
            [w / 2, h],      // Bottom center tip
            [0, h * 0.75],   // Bottom-left edge
            [0, 0],          // Top-left corner
        ];
        const insetPoints = [];
        const len = points.length;

        function getEdgeNormal(p1, p2) {
            const dx = p2[0] - p1[0];
            const dy = p2[1] - p1[1];
            const length = Math.hypot(dx, dy);
            return [-dy / length, dx / length]; // Perpendicular to the edge
        }

        function offsetPoint([x, y], [nx, ny], dist) {
            return [x + nx * dist, y + ny * dist];
        }

        function intersect([a1, a2], [b1, b2]) {
            const [x1, y1] = a1;
            const [x2, y2] = a2;
            const [x3, y3] = b1;
            const [x4, y4] = b2;

            const denom = (x1 - x2)*(y3 - y4) - (y1 - y2)*(x3 - x4);
            if (denom === 0) return null; // Parallel

            const px = ((x1*y2 - y1*x2)*(x3 - x4) - (x1 - x2)*(x3*y4 - y3*x4)) / denom;
            const py = ((x1*y2 - y1*x2)*(y3 - y4) - (y1 - y2)*(x3*y4 - y3*x4)) / denom;
            return [px, py];
        }

        // Compute offset edges
        const offsetEdges = [];

        for (let i = 0; i < len; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % len];
            const normal = getEdgeNormal(p1, p2);
            const p1Offset = offsetPoint(p1, normal, -offset); // inward
            const p2Offset = offsetPoint(p2, normal, -offset);
            offsetEdges.push([p1Offset, p2Offset]);
        }

        // Compute intersections
        for (let i = 0; i < len; i++) {
            const edge1 = offsetEdges[(i - 1 + len) % len];
            const edge2 = offsetEdges[i];
            const point = intersect(edge1, edge2);
            if (point) insetPoints.push(`${point[0]},${point[1]}`);
        }

        return insetPoints.join(' ');
    }

    window.addEventListener("load", resizeSVGToFitContent);
</script>